import { useEffect } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import TextInput from "common/blocks/TextInput";
import { useStockNumberSearch } from "common/layout/Header/hooks/useStockNumberSearch/useStockNumberSearch";
import { useForm } from "react-hook-form";
import type { ResolvedStockNumberSearch } from "resolvers/base/stockNumberSearchResolver";

import styles from "./StockNumberSearch.module.scss";
import { StockNumberSearchForm } from "./model";
import { stockNumberSearchFormSchema } from "./schema";

const cx = classNames.bind(styles);

export interface StockNumberSearchProps extends ResolvedStockNumberSearch {
    changeFocusStockInput?: (value: boolean) => void;
    onResize?: () => void;
}

const StockNumberSearch = ({
    changeFocusStockInput,
    errorText,
    buttonText,
    fieldName,
    onResize,
}: StockNumberSearchProps) => {
    const {
        formState: { errors },
        handleSubmit,
        register,
        setError,
        watch,
    } = useForm<StockNumberSearchForm>({
        defaultValues: {
            stockNumber: "",
        },
        mode: "onSubmit",
        resolver: zodResolver(stockNumberSearchFormSchema(errorText)),
        reValidateMode: "onSubmit",
    });
    const { handleStockNumberSearchForm } = useStockNumberSearch();

    useEffect(() => {
        if (errors?.stockNumber?.message && onResize) {
            onResize();
        }
    }, [errors?.stockNumber, onResize]);

    const onSubmit = async (formData: StockNumberSearchForm): Promise<void> => {
        if (onResize) {
            setTimeout(onResize, 0);
        }

        const { stockNumber } = formData;
        const isSuccess = await handleStockNumberSearchForm(stockNumber);
        if (!isSuccess) {
            setError("stockNumber", { type: "custom", message: errorText });
        }
    };

    return (
        <div className={cx("container")}>
            <form className={cx("form")} onSubmit={handleSubmit(onSubmit)}>
                <div className={cx("inputWrapper")}>
                    <TextInput
                        label={fieldName}
                        dataTestId={fieldName}
                        className={cx("stockNumberInput")}
                        labelClassName={cx("stockNumberInputLabel")}
                        hasError={!!errors?.stockNumber?.message}
                        errorText={errors?.stockNumber?.message}
                        value={watch("stockNumber")}
                        {...register("stockNumber")}
                        onBlur={() => {
                            if (changeFocusStockInput) {
                                changeFocusStockInput(false);
                            }
                        }}
                        onFocus={() => {
                            if (changeFocusStockInput) {
                                changeFocusStockInput(true);
                            }
                        }}
                    />
                </div>
                <div className={cx("buttonWrapper")}>
                    <Button
                        className={cx("buttonMobileClassName")}
                        theme="primary"
                        type="submit"
                        fullWidth
                    >
                        {buttonText}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default StockNumberSearch;
